/*
 * Copyright (c) 2014-2023 Inovalon, Inc. All Rights Reserved.
 * This computer program is CONFIDENTIAL and a TRADE SECRET of Inovalon, Inc.
 * The receipt or possession of this program does not convey any rights to use, reproduce or disclose
 * its contents in whole or in part, without the specific written consent of Inovalon, Inc.
 * Any use, reproduction or disclosure of this program without the express written consent of Inovalon, Inc.
 * is a violation of the copyright laws and may subject you to criminal prosecution.
 */
import { computed, action, observable } from 'mobx'
import { injectable } from 'inversify'
import { injectProperty } from '../../arc/configuration/ServiceLocator'
import { ICoverageDiscoveryResponseStateManager, IBaseResponse, IEligibilityBenfit, ISpell, IBaseSection, IBaseResponseDetail, IBaseResponsesFilterCriteria, ITransactionResponseEligiblity } from './contracts'
import { ValidationResult } from '../../arc/utils/ValidationResult/ValidationResult'
import { ICoverageDiscoveryResponseApiClient } from '../CoverageDiscoveryResponseApiClient/contracts'
import { SettingsManager } from '../../configuration/SettingsManager'
import { CommonUtils } from '../../utils/CommonUtils'

@injectable()
export class CoverageDiscoveryResponseStateManager implements ICoverageDiscoveryResponseStateManager {
	@injectProperty('ICoverageDiscoveryResponseApiClient')
	public CoverageDiscoveryResponseApiClient!: ICoverageDiscoveryResponseApiClient

	@observable
	payerList!: string[]
	@observable
	responsee!: IBaseResponse[]
	@observable
	selectedHospitalSpell?: ISpell[]
	@observable
	eligibilityBenefit!: IEligibilityBenfit[]
	@observable
	selectedSnfSection!: IBaseSection
	@observable
	selectedPreventativeSection!: IBaseSection
	@observable
	selectedPCPAlertsSection!: IBaseSection
	@observable
	selectedMcoStatusAlert!: IBaseSection
	@observable
	selectedOtherPayerAlert!: IBaseSection

	@observable
	selectedMedicareServiceCoverageSection!: IBaseSection
	@observable
	selectedMedicareInpatientSection!: IBaseSection
	@observable
	selectedMedicareHomeHospiceSection!: IBaseSection
	@observable
	selectedMedicareTherapyCapSection!: IBaseSection
	@observable
	selectedMedicareEligibilitySection!: IBaseSection
	@observable
	selectedMedicareStatusAlertSection!: IBaseSection
	@observable
	selectedMedicareServiceTypeSection!: IBaseSection

	@observable
	selectedServiceCoverageSection!: IBaseSection
	@observable
	selectedCEligibilitySection!: IBaseSection
	@observable
	selectedBenefitInformationSection!: IBaseSection
	@observable
	selectedCServiceTypeSection!: IBaseSection
	@observable
	selectedCovid19ImmunizationSection!: IBaseSection
	@observable
	baseResponsesFilterCriteria: IBaseResponsesFilterCriteria

	@observable
	responseId!: number
	@observable
	activeResponseEligibility: ITransactionResponseEligiblity | null
	@computed get validateSelectedRequestDetail(): ValidationResult {
		const result = new ValidationResult()

		return result
	}
	constructor() {
		this.responsee = []
		this.baseResponsesFilterCriteria = {
			sortField: 'Hierarchy',
			sortOrder: 'asc',
		}
		this.activeResponseEligibility = null
	}

	@action setResponse(Response: IBaseResponse[]): IBaseResponse[] {
		return (this.responsee = Response)
	}

	@action setFirstSection(sections: IBaseSection[]) {
		sections.forEach(x => {
			if (x.id === SettingsManager.SECTION_MEDICARE_COMMERCIAL_SERVICECOVERAGE) {
				this.selectedMedicareServiceCoverageSection = x
				this.selectedServiceCoverageSection = x
				return
			}

			if (x.id === SettingsManager.SECTION_MEDICARE_INPATIENT) {
				this.selectedMedicareInpatientSection = x
				return
			}

			if (x.id === SettingsManager.SECTION_MEDICARE_HOMEHOSPICE) {
				this.selectedMedicareHomeHospiceSection = x
				return
			}

			if (x.id === SettingsManager.SECTION_MEDICARE_THERAPYCAP) {
				this.selectedMedicareTherapyCapSection = x
				return
			}

			if (x.id === SettingsManager.SECTION_MEDICARE_ELIGIBILITY) {
				this.selectedMedicareEligibilitySection = x
				return
			}

			if (x.id === SettingsManager.SECTION_MEDICARE_STATUSALERT) {
				this.selectedMedicareStatusAlertSection = x
				return
			}

			if (x.id === SettingsManager.SECTION_MEDICARE_SERVICETYPE) {
				this.selectedMedicareServiceTypeSection = x
				return
			}

			if (x.id === SettingsManager.SECTION_PREVENTATIVE) {
				this.selectedPreventativeSection = x
				return
			}
			if (x.id === SettingsManager.SECTION_MEDICARE_COVID19IMMUNIZATIONSECTION) {
				this.selectedCovid19ImmunizationSection = x
				return
			}
			if (x.id === SettingsManager.SECTION_OTHERPAYERALERT) {
				this.selectedOtherPayerAlert = x
				return
			}
			if (x.id === SettingsManager.SECTION_STAY) {
				this.selectedSnfSection = x
				return
			}
			if (x.id === SettingsManager.SECTION_PCPALERTS) {
				this.selectedPCPAlertsSection = x
				return
			}
			if (x.id === SettingsManager.SECTION_MCOSTATUSALERT) {
				this.selectedMcoStatusAlert = x
				return
			}

			if (x.id === SettingsManager.SECTION_CELIGIBILITY) {
				this.selectedCEligibilitySection = x
				return
			}
			if (x.id === SettingsManager.SECTION_BENEFITINFORMATION) {
				this.selectedBenefitInformationSection = x
				return
			}
			if (x.id === SettingsManager.SECTION_CSERVICETYPE) {
				this.selectedCServiceTypeSection = x
				return
			}
		})
	}

	@action getBaseResponseDetails(transactionResponseId: number): Promise<IBaseResponseDetail[]> {
		return this.CoverageDiscoveryResponseApiClient.getBaseResponseDetails(transactionResponseId, this.baseResponsesFilterCriteria)
			.then(responses => {
				if (!responses || responses.length === 0) {
					return responses
				}
				return responses.sort((a, b) => CommonUtils.compareProperty(a.hierarchy, b.hierarchy)).slice(0, SettingsManager.RESPONSE_PAGE_MAX_PAYERS_TO_SHOW)
			})
			.catch(error => {
				console.log(error)
				throw new Error('An error occurred when updating the item')
			})
	}

	@action getResponse(id: number): Promise<IBaseResponse> {
		return this.CoverageDiscoveryResponseApiClient.getResponse(id)
			.then(response => {
				this.activeResponseEligibility = response.eligibilityResponseTransaction
				this.setFirstSection(response.sections)
				return response
			})
			.catch(error => {
				console.log(error)
				throw new Error('An error occurred when updating the item')
			})
	}
}
